// StartReturnNoIntegration is the entry page of the Lite portal flow
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import { Trans } from "react-i18next";

import { PageProps } from "../types";
import { RootReducer } from "../../redux/reducers";
import useSelector from "../../utility/useTypedSelector";
import { ReturnPortalBase } from "../../components/ReturnPortalBase";
import PrimaryButton from "../../components/Button/PrimaryButton";
import { ErrorAlertMessage } from "../../components/AlertMessage";
import { PrivacyFooter } from "../../components/PrivacyFooter";
import TextInput from "../../components/TextInput";
import { LoginBase } from "../LogIn";
import getTranslator from "../../utility/getTranslator";
import logger from "../../utility/logger/logger";
import { mapURLQueryStringToObject, returnStatusReturnIDQueryKey } from "../../utility";
import { goToPage } from "../../redux/app/actions";
import { setReturnStatusReturnID } from "../../redux/customer/actions";
import { useViewport } from "../../utility/useViewport";
import { MOBILE_BREAKPOINT } from "../../globalConstants";
import ga from "../../utility/GAEmitter";
import { AnalyticCategories, CommonPageActions } from "../../types/Analytics";
import { DataCyStrings } from "../../types/DataCyStrings";

import $StartReturnNoIntegration, {
  $ButtonRow,
  $Divider,
  $DropoffMethodsWrapper,
  $FieldRow,
  $FooterText,
  $Form,
  $InfoAlert,
  $InfoAlertIcon,
  $Section,
  $SectionLabel,
  $SelectField,
  $StepHeader,
  $StepHeaderSpacer,
  $StepHeaderTitle,
} from "./styles";
import { DropoffMethod, DropoffMethodCard, LocationDropoffMethodCard } from "../DropoffMethods";
import { DropoffMethodIDs } from "../../redux/customer/types";
import { SVG } from "../../components/svg";
import SVGButton from "../../components/SVGButton";

const dropoffMethodCardComponentsByID = {
  [DropoffMethodIDs.mailNoLabel]: LocationDropoffMethodCard,
};

const useTranslation = getTranslator("StartReturnNoIntegration");

// US 2-letter state codes, plus DC, US territories, and military APOs.
// Mirrors the server-side allowlist in modules/no_integration.go so a value
// that passes the client check will pass the server check too.
const US_STATE_CODES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE",
  "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS",
  "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS",
  "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY",
  "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV",
  "WI", "WY", "DC", "PR", "VI", "GU", "AS", "MP",
  "AA", "AE", "AP",
];

const ZIP_PATTERN = /^\d{5}(?:-\d{4})?$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormState {
  orderNumber: string;
  fullName: string;
  email: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  // dropoffMethodID is set when the retailer has either exactly one method
  // enabled (auto-picked from appConfig.dropoffMethods) or after the
  // shopper picks one on step 2.
  dropoffMethodID: string;
}

interface DropoffMethodSummary {
  id: string;
  label: string;
}

interface StartNoIntegrationReturnResponse {
  return: { id: string };
  returnStatusToken: string;
}

interface NoIntegrationDropoffMethodsResponse {
  dropoffMethods: DropoffMethod[];
  useDisplayNameOverrideForLocationName: boolean;
}

const emptyForm: FormState = {
  orderNumber: "",
  fullName: "",
  email: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  zip: "",
  dropoffMethodID: "",
};

const StartReturnNoIntegration = ({ page: _page }: PageProps) => {
  const dispatch = useDispatch();
  const app = useSelector((store: RootReducer) => store.app);
  const dropoffMethods: DropoffMethodSummary[] = app.dropoffMethods ?? [];
  const { t } = useTranslation();
  const { width } = useViewport();
  const isDesktop = width >= MOBILE_BREAKPOINT;

  // If return status ID is in the URL, dispatch it and navigate to the Return Status page
  const [pendingReturnStatusID] = useState<string>(() => {
    const queries = mapURLQueryStringToObject();
    return queries[returnStatusReturnIDQueryKey] ?? "";
  });

  useEffect(() => {
    if (!pendingReturnStatusID) return;
    dispatch(setReturnStatusReturnID(pendingReturnStatusID));
    dispatch(goToPage("returnStatus"));
  }, [pendingReturnStatusID, dispatch]);

  useEffect(() => {
    if (app.links?.favicon) {
      let headTitle = document?.querySelector("head");
      let favicon = headTitle?.querySelector('link[rel="icon"]');
      favicon?.setAttribute("href", app.links.favicon);
    }
  }, [app.links?.favicon]);

  // Retailer-customization hooks set via Dashboard → Customization. Each
  // is optional — render the default Lite portal experience when absent.
  const retailerLogoURL = app.links?.logo;
  const retailerHomeURL = app.links?.homeURL;
  const retailerName = app.copies?.retailerName;
  const retailerMessage = app.copies?.retailerMessage;
  const orderNumberHelpCopy = app.copies?.orderNumberMessage;

  // The form has at most two steps. Step 0 is always the field form; step
  // 1 is the dropoff picker, shown only when the retailer has 2+ enabled
  // dropoff methods. With a single method the picker is skipped and the
  // method is auto-selected before submit.
  const [step, setStep] = useState<0 | 1>(0);
  const [form, setForm] = useState<FormState>(() => {
    if (dropoffMethods.length === 1) {
      return { ...emptyForm, dropoffMethodID: dropoffMethods[0].id };
    }
    // default to mail no label if it is available
    if (dropoffMethods.some((m) => m.id === DropoffMethodIDs.mailNoLabel)) {
      return { ...emptyForm, dropoffMethodID: DropoffMethodIDs.mailNoLabel };
    }
    return emptyForm;
  });
  const [fieldError, setFieldError] = useState<string>("");
  const [submitError, setSubmitError] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [locationsToggleState, setLocationsToggleState] = useState<{ [index: number]: boolean }>({});
  const [fetchedDropoffMethods, setFetchedDropoffMethods] = useState<DropoffMethod[] | null>(null);
  const [fetchingDropoffMethods, setFetchingDropoffMethods] = useState<boolean>(false);

  const recaptchaRef = useRef<{ reset: Function; executeAsync: () => Promise<string | null> }>({
    reset: () => { },
    executeAsync: async () => null,
  });

  // requiresDropoffPicker is recomputed on every render so a /config
  // refresh during the same session is honored.
  const requiresDropoffPicker = useMemo(
    () => dropoffMethods.length > 1,
    [dropoffMethods.length],
  );

  // setField is shared between <TextInput> (which calls onChange with a
  // string value) and the native <select> (which calls onChange with an event).
  const setField = (name: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (fieldError) setFieldError("");
  };
  const onSelectField =
    (name: keyof FormState) =>
    (e: React.ChangeEvent<HTMLSelectElement>) =>
      setField(name, e.target.value);

  // validateFormStep enforces the same rules the server's
  // validateNoIntegrationRequest applies. Errors are user-visible — the
  // copy lives in the t() translation namespace.
  const validateFormStep = (): string => {
    if (!form.orderNumber.trim()) return t("errOrderNumberRequired");
    if (form.orderNumber.trim().length > 50) return t("errOrderNumberTooLong");
    if (!form.fullName.trim()) return t("errFullNameRequired");
    if (!EMAIL_PATTERN.test(form.email.trim())) return t("errEmailRequired");
    if (!form.line1.trim()) return t("errAddressLine1Required");
    if (!form.city.trim()) return t("errCityRequired");
    if (!form.state.trim()) return t("errStateRequired");
    if (!US_STATE_CODES.includes(form.state.trim().toUpperCase())) return t("errStateInvalid");
    if (!ZIP_PATTERN.test(form.zip.trim())) return t("errZipInvalid");
    return "";
  };

  const onFormStepNext = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validateFormStep();
    if (err) {
      setFieldError(err);
      return;
    }
    if (requiresDropoffPicker) {
      await fetchDropoffMethodsForStep1();
      setStep(1);
    } else {
      submit();
    }
  };

  // fetchDropoffMethodsForStep1 calls POST /noIntegrationDropoffMethods
  // with the shopper's address + a fresh ReCAPTCHA token, then stashes
  // the rich DropoffMethod list (with locations) in component state.
  const fetchDropoffMethodsForStep1 = async () => {
    setFetchingDropoffMethods(true);
    try {
      const body = {
        address: {
          line1: form.line1.trim(),
          line2: form.line2.trim(),
          city: form.city.trim(),
          state: form.state.trim().toUpperCase(),
          zip: form.zip.trim(),
        },
        locale: app.locale,
      };
      const resp = await axios.post<NoIntegrationDropoffMethodsResponse>(
        "/noIntegrationDropoffMethods",
        body,
      );
      setFetchedDropoffMethods(resp.data.dropoffMethods ?? []);
    } catch (err) {
      logger.Error("Error fetching no-integration dropoff methods", err);
    } finally {
      setFetchingDropoffMethods(false);
    }
  };

  const onDropoffSelect = (id: string) => {
    setForm((prev) => ({ ...prev, dropoffMethodID: id }));
  };

  const onBackToFormStep = () => {
    ga.event({
      category: AnalyticCategories.DropoffMethodsPage,
      action: CommonPageActions.BackArrow,
    });
    setFieldError("");
    setStep(0);
  };

  const onDropoffStepNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.dropoffMethodID) {
      setFieldError(t("errDropoffMethodRequired"));
      return;
    }
    submit();
  };

  // submit fires the POST to /startNoIntegrationReturn, including the
  // freshly-minted ReCAPTCHA token. On success it navigates to the existing
  // Return Status page
  const submit = async () => {
    setSubmitError("");
    setSubmitting(true);
    try {
      let recaptchaToken = "";
      if (app.recaptchaToken) {
        recaptchaToken = (await recaptchaRef.current.executeAsync()) ?? "";
      }

      const body = {
        orderNumber: form.orderNumber.trim(),
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        address: {
          line1: form.line1.trim(),
          line2: form.line2.trim(),
          city: form.city.trim(),
          state: form.state.trim().toUpperCase(),
          zip: form.zip.trim(),
        },
        dropoffMethodID: form.dropoffMethodID,
        recaptchaValue: recaptchaToken,
        locale: app.locale,
      };

      const resp = await axios.post<StartNoIntegrationReturnResponse>(
        "/startNoIntegrationReturn",
        body,
      );

      const { return: retrn, returnStatusToken } = resp.data;
      // navigate to return status page
      window.open(
        `/?returnStatusReturnID=${retrn.id}&locale=${app.locale ?? ""}&authorization=${returnStatusToken}`,
        "_self",
      );
    } catch (err) {
      logger.Error("Error starting no-integration return", err);
      // reset the captcha so a retry re-mints a fresh token.
      try { recaptchaRef.current.reset(); } catch { /* noop */ }
      setSubmitError(t("genericSubmitError"));
      setSubmitting(false);
    }
  };

  const renderFormStep = () => (
    <$Form onSubmit={onFormStepNext} aria-labelledby="start-no-integration-heading">
      {fieldError && <ErrorAlertMessage message={fieldError} />}

      <$Section>
        <$SectionLabel>{t("orderInformationLabel")}</$SectionLabel>

        <TextInput
          label={t("orderNumberLabel")}
          placeholder={t("orderNumberPlaceholder")}
          value={form.orderNumber}
          onChange={(v) => setField("orderNumber", v)}
          dataCyString={DataCyStrings.orderNumberInput}
        />

        {orderNumberHelpCopy && (
          <$InfoAlert role="note">
            <$InfoAlertIcon aria-hidden="true">
              <SVG name="info" />
            </$InfoAlertIcon>
            <span>{orderNumberHelpCopy}</span>
          </$InfoAlert>
        )}
      </$Section>

      <$Divider />

      <$Section>
        <$SectionLabel>{t("mailingInformationLabel")}</$SectionLabel>

        <TextInput
          label={t("fullNameLabel")}
          placeholder={t("fullNamePlaceholder")}
          value={form.fullName}
          onChange={(v) => setField("fullName", v)}
          dataCyString={DataCyStrings.fullNameInput}
        />

        <TextInput
          label={t("emailLabel")}
          type="email"
          placeholder={t("emailPlaceholder")}
          value={form.email}
          onChange={(v) => setField("email", v)}
          dataCyString={DataCyStrings.emailAddressInput}
        />

        <TextInput
          label={t("addressLine1Label")}
          placeholder={t("addressLine1Placeholder")}
          value={form.line1}
          onChange={(v) => setField("line1", v)}
          dataCyString={DataCyStrings.addressLine1Input}
        />

        <TextInput
          label={t("addressLine2Label")}
          placeholder={t("addressLine2Placeholder")}
          value={form.line2}
          onChange={(v) => setField("line2", v)}
          dataCyString={DataCyStrings.addressLine2Input}
        />

        <$FieldRow>
          <TextInput
            label={t("cityLabel")}
            placeholder={t("cityPlaceholder")}
            value={form.city}
            onChange={(v) => setField("city", v)}
            dataCyString={DataCyStrings.cityInput}
          />

          <$SelectField>
            <label htmlFor="start-no-integration-state">{t("stateLabel")}</label>
            <select
              id="start-no-integration-state"
              name="state"
              value={form.state}
              onChange={onSelectField("state")}
              required
              data-cy={DataCyStrings.stateSelect}
            >
              <option value="">{t("stateSelectPlaceholder")}</option>
              {US_STATE_CODES.map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </$SelectField>
        </$FieldRow>

        <TextInput
          label={t("zipLabel")}
          placeholder={t("zipPlaceholder")}
          value={form.zip}
          onChange={(v) => setField("zip", v)}
          dataCyString={DataCyStrings.zipCodeInput}
        />
      </$Section>

      <$ButtonRow>
        <PrimaryButton
          width="100%"
          disabled={submitting || fetchingDropoffMethods}
        >
          {requiresDropoffPicker ? t("nextButton") : t("startYourReturn")}
        </PrimaryButton>
      </$ButtonRow>

      <$Divider />

      <$FooterText>
        <Trans t={t} i18nKey="byContinuingYouAgree">
        By continuing, you acknowledge you have had an opportunity to read Happy Returns' <a target="_blank" href={`https://privacypolicy.happyreturns.com/${app.locale?.toLowerCase() || ""}`} rel="noreferrer">Privacy Policy</a>.
        </Trans>
      </$FooterText>
    </$Form>
  );

  // summaryToDropoffMethod adapts a Lite-portal DropoffMethodSummary
  // (label-only data from /config) into the DropoffMethod shape the
  // shared cards consume. Used as a fallback when /noIntegrationDropoffMethods
  // hasn't been called yet or its call failed
  const summaryToDropoffMethod = (m: DropoffMethodSummary): DropoffMethod => {
    const id = m.id as DropoffMethodIDs;
    let header = m.label;
    let info = "";
    if (id === DropoffMethodIDs.mailNoLabel) {
      header = t("methodTitleMailNoLabel");
      info = t("methodSubtitleMailNoLabel");
    } else if (id === DropoffMethodIDs.mail) {
      header = t("methodTitleMail");
    }
    return {
      id,
      header,
      info,
      badge: { color: "", text: "" },
      text: "",
      iconURL: "",
      url: "",
    };
  };

  const dropoffMethodDisplayPriority: Record<string, number> = {
    [DropoffMethodIDs.mailNoLabel]: 0,
    [DropoffMethodIDs.mail]: 1,
  };

  // Handling priority of showing mail-nolabel first then mail
  const resolvedDropoffMethods: DropoffMethod[] = fetchedDropoffMethods
    ? fetchedDropoffMethods
    : dropoffMethods.map(summaryToDropoffMethod);
  const orderedDropoffMethods = [...resolvedDropoffMethods].sort((a, b) => {
    const pa = dropoffMethodDisplayPriority[a.id] ?? 99;
    const pb = dropoffMethodDisplayPriority[b.id] ?? 99;
    return pa - pb;
  });

  const renderDropoffStep = () => (
    <$Form onSubmit={onDropoffStepNext} aria-labelledby="start-no-integration-dropoff-heading">
      <$StepHeader>
        <SVGButton
          onClick={onBackToFormStep}
          label={t("goBack")}
          svg="backArrow"
          dataCyString={DataCyStrings.leftNavigationHeaderButton}
        />
        <$StepHeaderTitle id="start-no-integration-dropoff-heading">
          {t("dropoffStepTitle")}
        </$StepHeaderTitle>
        <$StepHeaderSpacer aria-hidden="true" />
      </$StepHeader>

      {fieldError && <ErrorAlertMessage message={fieldError} />}

      <$DropoffMethodsWrapper>
        {orderedDropoffMethods.map((dropoffMethod, idx) => {
          const selected = form.dropoffMethodID === dropoffMethod.id;
          const Card = dropoffMethodCardComponentsByID[dropoffMethod.id] ?? DropoffMethodCard;
          return (
            <Card
              key={dropoffMethod.id}
              idx={idx}
              dropoffMethod={dropoffMethod}
              selected={selected}
              onClick={() => onDropoffSelect(dropoffMethod.id)}
              locationsToggleState={locationsToggleState}
              setLocationsToggleState={setLocationsToggleState}
            />
          );
        })}
      </$DropoffMethodsWrapper>

      <$ButtonRow>
        <PrimaryButton width="100%" disabled={submitting}>
          {t("startYourReturn")}
        </PrimaryButton>
      </$ButtonRow>
    </$Form>
  );

  // headerCopy is the bold copy under the retailer logo. Retailers can
  // override it via Dashboard. On step 1 the title is rendered inside
  // the step-header row alongside the back button, so we suppress the
  // LoginBase header to avoid duplicating it.
  const headerCopy =
    step === 0 ? (retailerMessage || t("title")) : "";

  // While the goToPage("returnStatus") dispatch is in flight, render
  // nothing so the form doesn't briefly paint before the Return Status
  // page takes over.
  if (pendingReturnStatusID) {
    return null;
  }

  return (
    <ReturnPortalBase>
      <>
        <$StartReturnNoIntegration className={step === 1 ? "is-step-dropoff" : ""}>
          <LoginBase
            logoURL={retailerLogoURL ?? ""}
            homeURL={retailerHomeURL ?? ""}
            retailerName={retailerName}
            header={headerCopy}
          >
            <>
              {step === 0 ? renderFormStep() : renderDropoffStep()}
              {submitError && <ErrorAlertMessage message={submitError} />}
            </>
          </LoginBase>

          {app.recaptchaToken && (
            <div className="g-recaptcha">
              <ReCAPTCHA
                ref={recaptchaRef as any}
                badge="bottomright"
                size="invisible"
                sitekey={app.recaptchaToken}
              />
            </div>
          )}

          {!isDesktop && <PrivacyFooter marginTop="auto" />}
        </$StartReturnNoIntegration>
        {isDesktop && <PrivacyFooter />}
      </>
    </ReturnPortalBase>
  );
};

export default StartReturnNoIntegration;
